/**
 * The Beu Support desk's stand-in data (admin side), until the backend's
 * support endpoints exist (docs/MESSAGES_BACKEND.md, sections 20 and 21).
 *
 * Every salon's tickets in one queue: what Bailey found, the conversation,
 * internal notes, Bailey's draft reply. Kept in this browser so a refresh
 * keeps what the team did; separate from the salon's own stand-in, so a
 * reply here doesn't reach a salon's chat until the backend joins them.
 */
const KEY = 'bm:admin-support:v1';
const minsAgo = (n) => new Date(Date.now() - n * 60000).toISOString();

export const PRIORITY = { urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low' };

function seed() {
    const t = (ref, extra) => ({
        ref, status: 'with-team', priority: 'normal', ownerId: null, createdAt: minsAgo(60), firstReplyAt: null,
        context: [], summary: '', draft: '', messages: [], ...extra,
    });
    const m = (from, ago, text, extra = {}) => ({ id: `${from}-${ago}-${text.length}`, from, at: minsAgo(ago), text, ...extra });
    return {
        me: { id: 'beu-tom', name: 'Tom' },
        team: [{ id: 'beu-tom', name: 'Tom' }, { id: 'beu-priya', name: 'Priya' }, { id: 'beu-amara', name: 'Amara' }],
        solvedByBaileyToday: 23,
        askedToday: 31,
        tickets: [
            t('BEU-1045', {
                title: "Can't take card payments", salon: { id: 'sal-glow', name: 'Glow Skin Clinic', mark: 'GS', colour: '#B4558F', plan: 'Pro plan', months: 7, owner: 'Hannah' },
                priority: 'urgent', createdAt: minsAgo(6), context: ['Point of Sale', 'iPhone', 'Beu app 4.12', 'Tap to Pay'],
                summary: 'Tap to Pay failed 3 times in the last 10 minutes with "terminal not ready". Last good payment 09:48. Card reader not paired.',
                draft: "Hi Hannah, Tom here. I'm on it now. While I look, you can take the payment as cash or a bank transfer and mark it paid at the till.",
                messages: [
                    m('salon', 6, "Urgent: I can't take payments or open the diary", { who: 'Hannah' }),
                    m('bailey', 6, "I've raised this as urgent and alerted the Beu team. Someone will be with you in a few minutes."),
                ],
            }),
            t('BEU-1042', {
                title: 'Card reader keeps disconnecting', salon: { id: 'sal-s9', name: 'Studio Nine', mark: 'S9', colour: '#29282D', plan: 'Pro plan', months: 14, owner: 'Ben' },
                priority: 'high', ownerId: 'beu-tom', createdAt: minsAgo(12), firstReplyAt: minsAgo(4), context: ['Point of Sale', 'iPad', 'Beu 4.12', 'WisePOS E, firmware 2.18'],
                summary: 'Reader dropped off Wi-Fi 4 times today. 2 payments retried, none lost. Firmware 2.18 is one behind, and 2.19 fixes this known drop-out.',
                draft: "All sorted Ben. I've updated your reader and it's been stable for the last hour. If it drops again, just reply here.",
                messages: [
                    m('salon', 12, 'The card reader keeps disconnecting during payments', { who: 'Ben' }),
                    m('bailey', 12, "I checked your reader: it dropped off Wi-Fi 4 times today and 2 payments had to be retried. That needs the team, so I've raised a ticket with everything I found."),
                    m('team', 4, "Hi Ben, Tom here. Could you move the reader onto your 5GHz Wi-Fi? I'm pushing a firmware update now.", { who: 'beu-tom' }),
                    m('note', 2, 'Firmware 2.19 pushed, reader back online.', { who: 'beu-tom' }),
                ],
            }),
            t('BEU-1041', {
                title: "Can't see last month's payout", salon: { id: 'sal-hart', name: 'Hart Barbers', mark: 'HB', colour: '#5B4B8A', plan: 'Starter plan', months: 3, owner: 'Marcus' },
                createdAt: minsAgo(38), context: ['Finance', 'Mac', 'Beu web'],
                summary: 'September payout of £2,140.50 was paid on 2 Oct to the account ending 0912. The Finance page filters to this month, so it is hidden.',
                draft: "Hi Marcus, your September payout of £2,140.50 went to your account ending 0912 on 2 October. In Finance, change the month at the top to September to see what was in it.",
                messages: [m('salon', 38, "I can't see last month's payout anywhere?", { who: 'Marcus' }), m('bailey', 37, "I couldn't fix that from here, so I've raised a ticket for the team with the page you were on and your device. They'll reply in this chat.")],
            }),
            t('BEU-1040', {
                title: 'Online booking shows the wrong hours', salon: { id: 'sal-pn', name: 'Polished Nail Bar', mark: 'PN', colour: '#B4558F', plan: 'Pro plan', months: 9, owner: 'Mia' },
                createdAt: minsAgo(65), context: ['Settings', 'iPhone', 'Beu app 4.12'],
                summary: 'Saturday hours are 9 to 5 in Settings, but Mia has a 9 to 1 working pattern on Saturdays, so online booking stops at 1.',
                draft: "Hi Mia, the booking page follows each person's working hours. Your Saturday pattern ends at 1pm, so it stops there. Change it in Team, under your hours.",
                messages: [m('salon', 65, 'Online booking shows the wrong hours on Saturdays', { who: 'Mia' }), m('bailey', 64, "I couldn't fix that from here, so I've raised a ticket for the team. They'll reply in this chat.")],
            }),
            t('BEU-1039', {
                title: 'Please merge two client profiles', salon: { id: 'sal-bl', name: 'Brow and Lash Room', mark: 'BL', colour: '#0E8A89', plan: 'Pro plan', months: 20, owner: 'Tara' },
                priority: 'low', ownerId: 'beu-priya', createdAt: minsAgo(120), firstReplyAt: minsAgo(100), context: ['Clients', 'Mac', 'Beu web'],
                summary: 'Two profiles for Ruth Evans (one with an old mobile). 9 bookings on one, 2 on the other.',
                messages: [m('salon', 120, 'Please could you merge the two Ruth Evans profiles?', { who: 'Tara' }), m('team', 100, "Hi Tara, Priya here. I'll merge them this afternoon and keep the newer mobile.", { who: 'beu-priya' })],
            }),
            t('BEU-1038', {
                title: 'Text reminders not sending', salon: { id: 'sal-glow', name: 'Glow Skin Clinic', mark: 'GS', colour: '#B4558F', plan: 'Pro plan', months: 7, owner: 'Hannah' },
                ownerId: 'beu-priya', createdAt: minsAgo(180), context: ['Marketing', 'Mac', 'Beu web'],
                summary: 'Text credit at 0 since 08:00. 14 reminders held. Auto top-up is off.',
                draft: "Hi Hannah, your text credit ran out this morning, so 14 reminders are waiting. Top up in Marketing, under Credits, and they'll go straight away. You can also switch on auto top-up there.",
                messages: [m('salon', 180, 'My text reminders are not sending', { who: 'Hannah' }), m('bailey', 179, "I couldn't fix that from here, so I've raised a ticket for the team. They'll reply in this chat.")],
            }),
            t('BEU-1031', {
                title: 'Deposit missing from receipt', salon: { id: 'sal-s9', name: 'Studio Nine', mark: 'S9', colour: '#29282D', plan: 'Pro plan', months: 14, owner: 'Ben' },
                status: 'fixed', ownerId: 'beu-amara', createdAt: minsAgo(60 * 24 * 10), firstReplyAt: minsAgo(60 * 24 * 10 - 20), context: ['Point of Sale', 'iPad', 'Beu 4.11'],
                summary: 'Deposits paid online were not printed on the final receipt.', rated: 'good',
                messages: [m('salon', 60 * 24 * 10, 'The deposit is missing from the receipt', { who: 'Ben' }), m('team', 60 * 24 * 10 - 20, "Thanks Ben, that's fixed in 4.12.", { who: 'beu-amara' })],
            }),
        ],
        solved: [
            { ref: 'S-2210', title: 'How do I add a team member?', salon: 'Studio Nine', at: minsAgo(30) },
            { ref: 'S-2209', title: 'How do I take a deposit for balayage only?', salon: 'Studio Nine', at: minsAgo(55) },
            { ref: 'S-2208', title: 'Where is my booking link?', salon: 'Hart Barbers', at: minsAgo(80) },
            { ref: 'S-2207', title: 'How do I block out time?', salon: 'Polished Nail Bar', at: minsAgo(140) },
        ],
    };
}

export function loadDesk() {
    try {
        const saved = JSON.parse(localStorage.getItem(KEY));
        if (saved && Array.isArray(saved.tickets)) return saved;
    } catch (e) { /* fall through to the seed */ }
    return seed();
}

export function saveDesk(desk) {
    try { localStorage.setItem(KEY, JSON.stringify(desk)); } catch (e) { /* storage full: this session still works */ }
}

export function resetDesk() {
    const fresh = seed();
    saveDesk(fresh);
    return fresh;
}

/** "6m", "1h 05m", "2d" since it was raised, for the queue. */
export function waitLabel(iso, now = Date.now()) {
    const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    if (mins < 60 * 24) { const h = Math.floor(mins / 60); const m = mins % 60; return m ? `${h}h ${m < 10 ? `0${m}` : m}m` : `${h}h`; }
    return `${Math.floor(mins / (60 * 24))}d`;
}

const ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

/** Open tickets: urgent first, then the longest waiting. */
export function queueOf(desk, view) {
    const open = desk.tickets.filter((t) => t.status !== 'fixed');
    const list = view === 'mine' ? open.filter((t) => t.ownerId === desk.me.id) : view === 'fixed' ? desk.tickets.filter((t) => t.status === 'fixed') : open;
    return list.slice().sort((a, b) => (ORDER[a.priority] - ORDER[b.priority]) || (new Date(a.createdAt) - new Date(b.createdAt)));
}

/** Median minutes from raised to the team's first reply, over tickets that have one. */
export function medianFirstReply(desk) {
    const mins = desk.tickets.filter((t) => t.firstReplyAt).map((t) => (new Date(t.firstReplyAt) - new Date(t.createdAt)) / 60000).sort((a, b) => a - b);
    if (!mins.length) return null;
    const mid = Math.floor(mins.length / 2);
    return Math.round(mins.length % 2 ? mins[mid] : (mins[mid - 1] + mins[mid]) / 2);
}

/** Pure updates the desk applies (the real ones are POSTs to the backend). */
export const deskActions = {
    take(desk, ref) {
        return { ...desk, tickets: desk.tickets.map((t) => (t.ref === ref ? { ...t, ownerId: desk.me.id } : t)) };
    },
    reply(desk, ref, text, { note = false, fix = false } = {}) {
        const at = new Date().toISOString();
        return {
            ...desk,
            tickets: desk.tickets.map((t) => {
                if (t.ref !== ref) return t;
                const msg = { id: `m-${Date.now()}`, from: note ? 'note' : 'team', at, text, who: desk.me.id };
                return {
                    ...t,
                    ownerId: t.ownerId || desk.me.id,
                    firstReplyAt: !note && !t.firstReplyAt ? at : t.firstReplyAt,
                    draft: note ? t.draft : '',
                    messages: t.messages.concat(msg),
                    status: fix ? 'fixed' : t.status,
                    fixedAt: fix ? at : t.fixedAt,
                };
            }),
        };
    },
    priority(desk, ref, priority) {
        return { ...desk, tickets: desk.tickets.map((t) => (t.ref === ref ? { ...t, priority } : t)) };
    },
    reopen(desk, ref) {
        return { ...desk, tickets: desk.tickets.map((t) => (t.ref === ref ? { ...t, status: 'with-team', fixedAt: null } : t)) };
    },
};
